import React, { CSSProperties, ReactNode } from 'react';
import { formatMessageBundle } from '@unifocus/js-utils';
import { Button, ButtonType } from '../button';
import messages from '../../../localized-messages';
import { HorizontalPanel } from '../../layout/horizontalpanel';
import { SubmitButton } from '../../data-entry/submit-button';

/**
 * {@docCategory ModalButtonBar}
 */
export interface ModalButtonBarProps {

   /**
    * Override the ok button text
    */
   okText?: React.ReactNode;

   /**
    * Override the ok button type
    */
   okType?: ButtonType | 'danger' | 'submit';

   /**
    * Override the cancel button text
    */
   cancelText?: React.ReactNode;

   /**
    * Whether or not to disable the ok button
    * @defaultvalue false
    */
   disableOk?: boolean;

   /**
    * Whether or not to disable the cancel button
    * @defaultvalue false
    */
   disableCancel?: boolean;

   /**
    * Whether or not to hide the ok button
    * @defaultvalue false
    */
   hideOk?: boolean;

   /**
    * Whether or not to hide the cancel button
    * @defaultvalue false
    */
   hideCancel?: boolean;

   /**
    * Use this for a custom modal button bar
    */
   buttons?: ModalButtonProps[];

   /**
    * Extra content on the left side of the button bar
    */
   leftContent?: ReactNode;

   /**
    * If the okButton is a submit type, this should be the name of the form that the submit button
    * is associated with.  Setting this also helps with submitting the form using enter key.
    */
   formName?: string;

   /**
    * Callback for when ok button is pressed
    * @param event - The mouse event
    */
   onOk?: (event: React.MouseEvent<HTMLElement>) => void;

   /**
    * Callback for when cancel button is pressed
    * @param event - The mouse event
    */
   onCancel?: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * {@docCategory ModalButtonBar}
 */
export interface ModalButtonProps {
   /**
    * The text to display for the button.
    */
   text?: string;

   /**
    * The type of the button.
    */
   type?: ButtonType;

   /**
    * The icon to display for the button.
    */
   icon?: ReactNode;

   /**
    * Enables ghost mode.
    */
   ghost?: boolean;

   /**
    * Enables danger mode.
    */
   danger?: boolean;

   /**
    * Disables the buttons.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Callback for onClick
    */
   onClick?: React.MouseEventHandler<HTMLElement>;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;
}

export const ModalButtonBar = (props: ModalButtonBarProps) => {
   return (
      <HorizontalPanel gap={'small'} className={'uf-modal-button-bar'} horizontalFill={true}>
         {
            props.leftContent ?
               <div className={'uf-modal-button-bar-extra'}>
                  {props.leftContent}
               </div> : null
         }
         <HorizontalPanel align={'right'} gap={'small'} className={'uf-modal-button-bar-buttons'} horizontalFill={true}>
            {buttonBarContent(props)}
         </HorizontalPanel>
      </HorizontalPanel>
   );
};

const buttonBarContent = (props: ModalButtonBarProps) => {
   if (Array.isArray(props.buttons) && props.buttons.length > 0) {
      return customButtonBarContent(props.buttons);
   }

   return standardButtonBarContent(props);
};

const standardButtonBarContent = (props: ModalButtonBarProps) => {
   const okType = (!props.okType || props.okType === 'danger' || props.okType === 'submit') ? 'primary' : props.okType;

   const okButton = props.okType==='submit' ?
                    (<SubmitButton
                       type={okType}
                       disabled={props.disableOk}
                       formName={props.formName}
                    >
                       {props.okText || formatMessageBundle(messages, 'res_ok')}
                    </SubmitButton>) :
                    (<Button
                       type={okType}
                       danger={props.okType === 'danger'}
                       onClick={props.onOk}
                       disabled={props.disableOk}
                    >
                       {props.okText || formatMessageBundle(messages, 'res_ok')}
                    </Button>);

   return (
      <>
         {!props.hideCancel && <Button
            onClick={props.onCancel}
            disabled={props.disableCancel}
            data-iscancelbutton={true}
         >
            {props.cancelText || formatMessageBundle(messages, 'res_cancel')}
         </Button>}

         {!props.hideOk && okButton}
      </>
   );
};

const customButtonBarContent = (buttons: ModalButtonProps[]) => {
   return buttons.map((button, index) => {
      return (
         <Button
            key={index}
            type={button.type}
            icon={button.icon}
            ghost={button.ghost}
            danger={button.danger}
            disabled={button.disabled}
            onClick={button.onClick}
            className={button.className}
            style={button.style}
         >
            {button.text}
         </Button>
      );
   });
};
